import type {
  AclRoleDto,
  AclRoleResourceDto,
  AclRoleResourceVMPaginationResponse,
  AclRoleVMPaginationResponse,
  BaseErrorResponse,
  EmptySuccessResponse,
  GetAclRoleResourceVMEntityRes,
  GetAclRoleVMEntityRes,
} from './data-contracts';
import type { RequestParams } from './http-client';
import { ContentType, HttpClient } from './http-client';


export default function useAclRole() {
  class AclRole<SecurityDataType = unknown> extends HttpClient<SecurityDataType> {
    /**
     * No description
     *
     * @tags ACL Role
     * @name AclRoleControllerDeleteEntity
     * @summary Acl Role delete
     * @request DELETE:/api3/acl-role/{id}
     * @secure
     * @response `200` `EmptySuccessResponse`
     * @response `400` `BaseErrorResponse`
     */
    aclRoleControllerDeleteEntity = (id: string, params: RequestParams = {}) =>
      this.request<EmptySuccessResponse, BaseErrorResponse>({
        path: `/api3/acl-role/${id}`,
        method: 'DELETE',
        secure: true,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags ACL Role
     * @name AclRoleControllerUpdateEntity
     * @summary Acl Role update
     * @request PUT:/api3/acl-role/{id}
     * @secure
     * @response `200` `GetAclRoleVMEntityRes`
     * @response `400` `BaseErrorResponse`
     */
    aclRoleControllerUpdateEntity = (id: string, data: AclRoleDto, params: RequestParams = {}) =>
      this.request<GetAclRoleVMEntityRes, BaseErrorResponse>({
        path: `/api3/acl-role/${id}`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags ACL Role
     * @name AclRoleControllerGetEntityByPk
     * @summary Get Acl Role by PK
     * @request GET:/api3/acl-role/{id}
     * @secure
     * @response `200` `GetAclRoleVMEntityRes`
     * @response `400` `BaseErrorResponse`
     */
    aclRoleControllerGetEntityByPk = (id: string, params: RequestParams = {}) =>
      this.request<GetAclRoleVMEntityRes, BaseErrorResponse>({
        path: `/api3/acl-role/${id}`,
        method: 'GET',
        secure: true,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags ACL Role
     * @name AclRoleControllerCreateEntity
     * @summary Acl Role create
     * @request POST:/api3/acl-role
     * @secure
     * @response `200` `GetAclRoleVMEntityRes`
     * @response `400` `BaseErrorResponse`
     */
    aclRoleControllerCreateEntity = (data: AclRoleDto, params: RequestParams = {}) =>
      this.request<GetAclRoleVMEntityRes, BaseErrorResponse>({
        path: '/api3/acl-role',
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags ACL Role
     * @name AclRoleControllerGetAllPaginatedEntities
     * @summary Get Acl Roles
     * @request GET:/api3/acl-role
     * @secure
     * @response `200` `AclRoleVMPaginationResponse`
     * @response `400` `BaseErrorResponse`
     */
    aclRoleControllerGetAllPaginatedEntities = (
      query?: {
        /**
         * @min 1
         * @max 500
         * @example 10
         */
        limit?: number;
        /**
         * @min 0
         * @example 0
         */
        page?: number;
        /** @example [{"field":"acl-role.id","operation":"{ != | = | > | >= | < | <= | between | n-between | in | n-in }","value":null},{"field":"acl-role.title","operation":"{ != | = | in | n-in | like | iLike | n-like | n-iLike }","value":null},{"field":"acl-role.isAdmin","operation":"{ != | = }","value":null},{"field":"acl-role.isSystem","operation":"{ != | = }","value":null},{"field":"acl-role.alias","operation":"{ != | = | in | n-in | like | iLike | n-like | n-iLike }","value":null},{"field":"acl-role.aclRoleGroupId","operation":"{ != | = | > | >= | < | <= | between | n-between | in | n-in }","value":null},{"field":"acl-role-resource.id","operation":"{ != | = | > | >= | < | <= | between | n-between | in | n-in }","value":null},{"field":"acl-role-resource.resource","operation":"{ != | = | in | n-in | like | iLike | n-like | n-iLike }","value":null}] */
        filter?: object;
        /**
         * Sort options
         * @example {"acl-role.id":"{ ASC | DESC }","acl-role.title":"{ ASC | DESC }","acl-role.isAdmin":"{ ASC | DESC }","acl-role.isSystem":"{ ASC | DESC }","acl-role.alias":"{ ASC | DESC }","acl-role.aclRoleGroupId":"{ ASC | DESC }","acl-role-resource.id":"{ ASC | DESC }","acl-role-resource.resource":"{ ASC | DESC }"}
         */
        orderBy?: object;
        /** @example false */
        includeRoleResources?: boolean;
      },
      params: RequestParams = {},
    ) =>
      this.request<AclRoleVMPaginationResponse, BaseErrorResponse>({
        path: '/api3/acl-role',
        method: 'GET',
        query,
        secure: true,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags ACL Role Resource
     * @name AclRoleResourceControllerDeleteEntity
     * @summary Acl Role Resource delete
     * @request DELETE:/api3/acl-role/{aclRoleId}/resource/{id}
     * @secure
     * @response `200` `EmptySuccessResponse`
     * @response `400` `BaseErrorResponse`
     */
    aclRoleResourceControllerDeleteEntity = (aclRoleId: number, id: string, params: RequestParams = {}) =>
      this.request<EmptySuccessResponse, BaseErrorResponse>({
        path: `/api3/acl-role/${aclRoleId}/resource/${id}`,
        method: 'DELETE',
        secure: true,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags ACL Role Resource
     * @name AclRoleResourceControllerUpdateEntity
     * @summary Acl Role Resource update
     * @request PUT:/api3/acl-role/{aclRoleId}/resource/{id}
     * @secure
     * @response `200` `GetAclRoleResourceVMEntityRes`
     * @response `400` `BaseErrorResponse`
     */
    aclRoleResourceControllerUpdateEntity = (
      aclRoleId: number,
      id: string,
      data: AclRoleResourceDto,
      params: RequestParams = {},
    ) =>
      this.request<GetAclRoleResourceVMEntityRes, BaseErrorResponse>({
        path: `/api3/acl-role/${aclRoleId}/resource/${id}`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags ACL Role Resource
     * @name AclRoleResourceControllerGetEntityByPk
     * @summary Get Acl Role Resource by PK
     * @request GET:/api3/acl-role/{aclRoleId}/resource/{id}
     * @secure
     * @response `200` `GetAclRoleResourceVMEntityRes`
     * @response `400` `BaseErrorResponse`
     */
    aclRoleResourceControllerGetEntityByPk = (aclRoleId: number, id: string, params: RequestParams = {}) =>
      this.request<GetAclRoleResourceVMEntityRes, BaseErrorResponse>({
        path: `/api3/acl-role/${aclRoleId}/resource/${id}`,
        method: 'GET',
        secure: true,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags ACL Role Resource
     * @name AclRoleResourceControllerCreateEntity
     * @summary Acl Role Resource create
     * @request POST:/api3/acl-role/{aclRoleId}/resource
     * @secure
     * @response `200` `GetAclRoleResourceVMEntityRes`
     * @response `400` `BaseErrorResponse`
     */
    aclRoleResourceControllerCreateEntity = (aclRoleId: number, data: AclRoleResourceDto, params: RequestParams = {}) =>
      this.request<GetAclRoleResourceVMEntityRes, BaseErrorResponse>({
        path: `/api3/acl-role/${aclRoleId}/resource`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags ACL Role Resource
     * @name AclRoleResourceControllerGetAllPaginatedEntities
     * @summary Get Acl Role Resources
     * @request GET:/api3/acl-role/{aclRoleId}/resource
     * @secure
     * @response `200` `AclRoleResourceVMPaginationResponse`
     * @response `400` `BaseErrorResponse`
     */
    aclRoleResourceControllerGetAllPaginatedEntities = (
      aclRoleId: number,
      query?: {
        /**
         * @min 1
         * @max 500
         * @example 10
         */
        limit?: number;
        /**
         * @min 0
         * @example 0
         */
        page?: number;
        /** @example [{"field":"acl-role-resource.id","operation":"{ != | = | > | >= | < | <= | between | n-between | in | n-in }","value":null},{"field":"acl-role-resource.resource","operation":"{ != | = | in | n-in | like | iLike | n-like | n-iLike }","value":null}] */
        filter?: object;
        /**
         * Sort options
         * @example {"acl-role-resource.id":"{ ASC | DESC }","acl-role-resource.resource":"{ ASC | DESC }"}
         */
        orderBy?: object;
      },
      params: RequestParams = {},
    ) =>
      this.request<AclRoleResourceVMPaginationResponse, BaseErrorResponse>({
        path: `/api3/acl-role/${aclRoleId}/resource`,
        method: 'GET',
        query,
        secure: true,
        format: 'json',
        ...params,
      });
  }

  return new AclRole();
}
